(() => {
  'use strict';

  if (window.fitwoodCreditInitialized) return;
  window.fitwoodCreditInitialized = true;

  const SELECTOR = '[data-fitwood-credit]';
  const CREDIT_RATE = 15;

  const EU_COUNTRIES = new Set([
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK',
    'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
    'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SE', 'SI', 'SK'
  ]);

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;

  let timer;
  let loading = false;
  let refreshAgain = false;

  function calculators() {
    return Array.from(document.querySelectorAll(SELECTOR));
  }

  function isB2B() {
    return document.body.classList.contains('customer-b2b');
  }

  function getEstimatedCreditBase(cart) {
    const total = Number(cart.total_price);

    if (!Number.isFinite(total) || total < 0) {
      return null;
    }

    const element = document.querySelector(SELECTOR);
    const country = element?.dataset.countryCode?.toUpperCase();

    // EU: hintaan sisältyviä veroja ei vähennetä.
    if (EU_COUNTRIES.has(country)) {
      return total;
    }

    // Säilytetään aiemman laskurin toiminta EU:n ulkopuolella.
    const liquidTaxesIncluded = element?.dataset.taxesIncluded;
    let taxesIncluded = null;

    if (typeof cart.taxes_included === 'boolean') {
      taxesIncluded = cart.taxes_included;
    } else if (liquidTaxesIncluded === 'true') {
      taxesIncluded = true;
    } else if (liquidTaxesIncluded === 'false') {
      taxesIncluded = false;
    }

    if (taxesIncluded === false) {
      return total;
    }

    if (taxesIncluded !== true || !country) {
      return null;
    }

    return total;
  }

  function setText(element, value) {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  }

  function template(text, values) {
    let result = text || '';

    Object.entries(values).forEach(([key, value]) => {
      result = result.split(`[[${key}]]`).join(String(value));
    });

    return result;
  }

  function formatMoney(cents, currency) {
    const locale =
      document.documentElement.lang ||
      window.Shopify?.locale ||
      'en';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(cents / 100);
  }

  function hideAll() {
    calculators().forEach((element) => {
      element.hidden = true;
    });
  }

  function render(cart) {
    const total = Number(cart.total_price);
    const currency = cart.currency;

    const eligible =
      typeof currency === 'string' &&
      /^[A-Z]{3}$/.test(currency) &&
      Number(cart.item_count) > 0 &&
      Number.isFinite(total) &&
      total >= 0 &&
      !isB2B();

    if (!eligible) {
      hideAll();
      return;
    }

    const creditBase = getEstimatedCreditBase(cart);

    // Ilman laskentapohjaa ei näytetä arviota.
    if (creditBase === null) {
      hideAll();
      return;
    }

    // Kiinteä 15 % ostoskorin omassa valuutassa.
    const credit = Math.round(creditBase * CREDIT_RATE / 100);
    const formattedCredit = formatMoney(credit, currency);

    calculators().forEach((element) => {
      const summary = element.querySelector(
        '.fitwood-credit__summary'
      );

      if (summary) {
        summary.hidden = false;
      }

      setText(
        element.querySelector('[data-credit-amount]'),
        formattedCredit
      );

      setText(
        element.querySelector('[data-credit-rate]'),
        template(element.dataset.rateTemplate, {
          rate: CREDIT_RATE
        }) || `${CREDIT_RATE}%`
      );

      element.hidden = false;
    });
  }

  async function refresh() {
    if (!calculators().length) return;

    if (isB2B()) {
      hideAll();
      return;
    }

    if (loading) {
      refreshAgain = true;
      return;
    }

    loading = true;

    try {
      const response = await fetch(cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Cart request failed: ${response.status}`
        );
      }

      const cart = await response.json();
      render(cart);
    } catch (error) {
      // Epäonnistuneen päivityksen jälkeen piilotetaan vanha arvio.
      hideAll();
      console.warn('[Fitwood credit]', error);
    } finally {
      loading = false;

      if (refreshAgain) {
        refreshAgain = false;
        scheduleRefresh();
      }
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(timer);
    timer = window.setTimeout(refresh, 180);
  }

  function isOnScreen(element) {
    const rect = element.getBoundingClientRect();

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.right > 0 &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.top < window.innerHeight &&
      window.getComputedStyle(element).visibility !== 'hidden'
    );
  }

  function start() {
    if (isB2B()) {
      hideAll();
      return;
    }

    refresh();

    // Teeman ostoskoritapahtumat.
    [
      'theme:cart:change',
      'theme:cart:added',
      'theme:cart:open',
      'cart:updated',
      'cart:refresh'
    ].forEach((eventName) => {
      document.addEventListener(eventName, scheduleRefresh);
    });

    document.addEventListener('change', (event) => {
      if (
        event.target instanceof Element &&
        event.target.matches(
          '[name="updates[]"], [name="quantity"], [data-quantity-input]'
        )
      ) {
        scheduleRefresh();
      }
    });

    // Päivitys ostoskorin muutospyyntöjen valmistuttua.
    if (
      'PerformanceObserver' in window &&
      PerformanceObserver.supportedEntryTypes?.includes('resource')
    ) {
      const requestObserver = new PerformanceObserver((list) => {
        const cartChanged = list.getEntries().some((entry) => {
          try {
            const url = new URL(entry.name);

            return (
              url.origin === window.location.origin &&
              /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(
                url.pathname
              )
            );
          } catch {
            return false;
          }
        });

        if (cartChanged) {
          scheduleRefresh();
        }
      });

      requestObserver.observe({
        type: 'resource',
        buffered: false
      });
    }

    // Päivitys, jos teema korvaa drawerin HTML-sisällön.
    const drawerObserver = new MutationObserver((mutations) => {
      const calculatorAdded = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) =>
          node instanceof Element &&
          (
            node.matches(SELECTOR) ||
            node.querySelector(SELECTOR)
          )
        )
      );

      if (calculatorAdded) {
        scheduleRefresh();
      }
    });

    drawerObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Varmistuspäivitys laskurin ollessa näkyvissä.
    window.setInterval(() => {
      if (
        !document.hidden &&
        calculators().some(isOnScreen)
      ) {
        refresh();
      }
    }, 2000);

    window.addEventListener('pageshow', scheduleRefresh);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        scheduleRefresh();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();